import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  AdminStatusBadge,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  ConfirmDialog,
  AdminSectionHeading,
  AdminStatCard,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconDuplicate,
  IconEdit,
  IconGeneral,
  IconMetadata,
  IconRelationships,
} from './components/AdminIcons';

interface Classroom {
  id: number;
  organizationId: number;
  name: string;
  code?: string | null;
  teacherUserId?: number | null;
  isActive: boolean;
  members: Array<{ id: number; userId: number; role: string; status: string }>;
  courses: Array<{ id: number; courseId: number; isDefault: boolean }>;
}

interface Course {
  id: number;
  name: string;
}

interface UserOption {
  userId: number;
  displayName: string;
  email?: string | null;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;
const DEFAULT_PAGE_SIZE = 8;

type EditorMode = 'view' | 'create' | 'edit';
type SortKey = 'name' | 'code' | 'members' | 'status';

interface ClassroomRow {
  id: number;
  name: string;
  code: string;
  members: number;
  status: 'Active' | 'Archived';
  classroom: Classroom;
}

interface ConfirmDialogState {
  title: string;
  message: string;
  actions: Array<{
    label: string;
    variant?: 'default' | 'primary' | 'destructive';
    onClick: () => void;
    disabled?: boolean;
  }>;
}

const emptyClassroomForm = {
  name: '',
  code: '',
  teacherUserId: '',
  isActive: true,
};

const ManageClassrooms: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(null);
  const [mode, setMode] = useState<EditorMode>('view');
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [classroomForm, setClassroomForm] = useState(emptyClassroomForm);
  const [memberUserId, setMemberUserId] = useState<number | null>(null);
  const [memberUserSearch, setMemberUserSearch] = useState('');
  const [memberRole, setMemberRole] = useState('Student');
  const [courseIdToAdd, setCourseIdToAdd] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);
  const [removingCourseLinkId, setRemovingCourseLinkId] = useState<number | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!hasActiveOrganization) {
        setClassrooms([]);
        setCourses([]);
        setUsers([]);
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      const [classroomResponse, courseResponse, userResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/classrooms`, { headers: orgHeaders }),
        axios.get(`${API_BASE_URL}/courses`, { headers: orgHeaders }),
        axios.get(`${API_BASE_URL}/classrooms/users`, { headers: orgHeaders })
      ]);
      setClassrooms(Array.isArray(classroomResponse.data) ? classroomResponse.data : []);
      const loadedCourses = Array.isArray(courseResponse.data) ? courseResponse.data : [];
      setCourses(loadedCourses);
      setUsers(Array.isArray(userResponse.data) ? userResponse.data : []);
      if (loadedCourses.length > 0) {
        setCourseIdToAdd((prev) => prev ?? loadedCourses[0].id);
      }
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load classrooms.');
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const selectedClassroom = useMemo(
    () => classrooms.find((c) => c.id === selectedClassroomId) || null,
    [classrooms, selectedClassroomId]
  );

  const rows = useMemo<ClassroomRow[]>(
    () => classrooms.map((classroom) => ({
      id: classroom.id,
      name: classroom.name,
      code: classroom.code || 'N/A',
      members: classroom.members.length,
      status: classroom.isActive ? 'Active' : 'Archived',
      classroom,
    })),
    [classrooms]
  );

  const filteredRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();

    return rows.filter((row) => {
      if (search) {
        const haystack = [row.name, row.code, row.status].join(' ').toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (statusFilter !== 'all' && row.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [rows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const direction = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((first, second) => {
      if (sortKey === 'members') {
        return (first.members - second.members) * direction;
      }

      const leftValue = String(first[sortKey]).toLowerCase();
      const rightValue = String(second[sortKey]).toLowerCase();

      if (leftValue < rightValue) {
        return -1 * direction;
      }

      if (leftValue > rightValue) {
        return 1 * direction;
      }

      return 0;
    });

    return sorted;
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const filteredUsers = users.filter((user) => {
    const search = memberUserSearch.trim().toLowerCase();
    if (!search) {
      return true;
    }

    return user.displayName.toLowerCase().includes(search)
      || (user.email || '').toLowerCase().includes(search)
      || String(user.userId).includes(search);
  });

  const beginCreate = () => {
    setMode('create');
    setSelectedClassroomId(null);
    setClassroomForm(emptyClassroomForm);
    setMemberUserId(null);
    setMemberUserSearch('');
    setMessage(null);
    setError(null);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const beginEdit = (classroom: Classroom) => {
    setSelectedClassroomId(classroom.id);
    setMode('edit');
    setClassroomForm({
      name: classroom.name,
      code: classroom.code || '',
      teacherUserId: classroom.teacherUserId ? String(classroom.teacherUserId) : '',
      isActive: classroom.isActive,
    });
    setMessage(null);
    setError(null);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const selectClassroom = (classroom: Classroom) => {
    setSelectedClassroomId(classroom.id);
    setMode('view');
    setClassroomForm({
      name: classroom.name,
      code: classroom.code || '',
      teacherUserId: classroom.teacherUserId ? String(classroom.teacherUserId) : '',
      isActive: classroom.isActive,
    });
    setMessage(null);
    setError(null);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const createClassroom = async () => {
    if (!classroomForm.name.trim()) {
      setError('Classroom name is required.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      if (!hasActiveOrganization) {
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      await axios.post(
        `${API_BASE_URL}/classrooms`,
        {
          name: classroomForm.name.trim(),
          code: classroomForm.code.trim() || null,
          teacherUserId: classroomForm.teacherUserId.trim() ? Number(classroomForm.teacherUserId) : null,
          isActive: classroomForm.isActive
        },
        { headers: orgHeaders }
      );
      setClassroomForm(emptyClassroomForm);
      setMode('view');
      setMessage('Classroom created.');
      void loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create classroom.');
    }
  };

  const updateClassroom = async () => {
    if (!selectedClassroomId) {
      setError('Select a classroom to edit.');
      return;
    }

    if (!classroomForm.name.trim()) {
      setError('Classroom name is required.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      if (!hasActiveOrganization) {
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      await axios.put(
        `${API_BASE_URL}/classrooms/${selectedClassroomId}`,
        {
          name: classroomForm.name.trim(),
          code: classroomForm.code.trim() || null,
          teacherUserId: classroomForm.teacherUserId.trim() ? Number(classroomForm.teacherUserId) : null,
          isActive: classroomForm.isActive,
        },
        { headers: orgHeaders }
      );

      setMessage('Classroom updated.');
      setMode('view');
      void loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update classroom.');
    }
  };

  const addMember = async () => {
    if (!selectedClassroomId || !memberUserId) {
      setError('Select a classroom and user.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      if (!hasActiveOrganization) {
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      await axios.post(
        `${API_BASE_URL}/classrooms/${selectedClassroomId}/members`,
        { userId: memberUserId, role: memberRole },
        { headers: orgHeaders }
      );
      setMemberUserId(null);
      setMemberUserSearch('');
      setMessage('Member added/updated.');
      void loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to add member.');
    }
  };

  const addCourse = async () => {
    if (!selectedClassroomId || !courseIdToAdd) {
      setError('Select a classroom and course.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      if (!hasActiveOrganization) {
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      await axios.post(
        `${API_BASE_URL}/classrooms/${selectedClassroomId}/courses`,
        { courseId: courseIdToAdd, isDefault: true },
        { headers: orgHeaders }
      );
      setMessage('Course linked to classroom.');
      void loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to add course.');
    }
  };

  const removeMember = async (membershipId: number) => {
    if (!selectedClassroomId) {
      setError('Select a classroom first.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      if (!hasActiveOrganization) {
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      setRemovingMemberId(membershipId);
      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      await axios.delete(`${API_BASE_URL}/classrooms/${selectedClassroomId}/members/${membershipId}`, {
        headers: orgHeaders,
      });
      setMessage('Member removed from classroom.');
      void loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to remove member.');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const removeCourse = async (classroomCourseId: number) => {
    if (!selectedClassroomId) {
      setError('Select a classroom first.');
      return;
    }

    try {
      setError(null);
      setMessage(null);
      if (!hasActiveOrganization) {
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      setRemovingCourseLinkId(classroomCourseId);
      const token = await getManagementToken();
      const orgHeaders = getOrganizationHeaders(token);
      await axios.delete(`${API_BASE_URL}/classrooms/${selectedClassroomId}/courses/${classroomCourseId}`, {
        headers: orgHeaders,
      });
      setMessage('Course removed from classroom.');
      void loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to remove course.');
    } finally {
      setRemovingCourseLinkId(null);
    }
  };

  const closeConfirmDialog = () => setConfirmDialog(null);

  const requestRemoveMember = (membershipId: number, memberName: string) => {
    setConfirmDialog({
      title: 'Remove assigned member?',
      message: `Are you sure you want to remove ${memberName} from this classroom?`,
      actions: [
        {
          label: 'Cancel',
          variant: 'default',
          onClick: closeConfirmDialog,
        },
        {
          label: removingMemberId === membershipId ? 'Removing...' : 'Remove',
          variant: 'destructive',
          disabled: removingMemberId !== null || removingCourseLinkId !== null,
          onClick: () => {
            closeConfirmDialog();
            void removeMember(membershipId);
          },
        },
      ],
    });
  };

  const requestRemoveCourse = (classroomCourseId: number, courseName: string) => {
    setConfirmDialog({
      title: 'Remove linked course?',
      message: `Are you sure you want to remove ${courseName} from this classroom?`,
      actions: [
        {
          label: 'Cancel',
          variant: 'default',
          onClick: closeConfirmDialog,
        },
        {
          label: removingCourseLinkId === classroomCourseId ? 'Removing...' : 'Remove',
          variant: 'destructive',
          disabled: removingMemberId !== null || removingCourseLinkId !== null,
          onClick: () => {
            closeConfirmDialog();
            void removeCourse(classroomCourseId);
          },
        },
      ],
    });
  };

  const handleSort = (columnKey: string) => {
    if (!['name', 'code', 'members', 'status'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const columns = useMemo<DataGridColumn<ClassroomRow>[]>(() => {
    const baseColumns: DataGridColumn<ClassroomRow>[] = [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        width: isSmallScreen ? '65%' : '34%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>Code: {row.code}</div>
          </div>
        ),
      },
    ];

    if (!isSmallScreen) {
      baseColumns.push({
        key: 'members',
        label: 'Members',
        sortable: true,
        width: '20%',
        render: (row) => row.members,
      });
    }

    baseColumns.push({
      key: 'status',
      label: 'Status',
      sortable: true,
      width: isSmallScreen ? '20%' : '16%',
      render: (row) => <AdminStatusBadge status={row.status} />,
    });

    if (!isSmallScreen) {
      baseColumns.push({
        key: 'code',
        label: 'Code',
        sortable: true,
        width: '20%',
        render: (row) => row.code,
      });
    }

    return baseColumns;
  }, [isSmallScreen]);

  const filterConfigs = useMemo<FilterConfig[]>(() => ([
    {
      key: 'status',
      label: 'Status',
      value: statusFilter,
      onChange: (value) => {
        setStatusFilter(value);
        setPage(1);
      },
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Archived', label: 'Archived' },
      ],
    },
  ]), [statusFilter]);

  const rowActions: DataGridRowAction<ClassroomRow>[] = [
    {
      key: 'open',
      label: 'Open classroom',
      icon: <IconDuplicate size={14} />,
      onClick: (row) => selectClassroom(row.classroom),
      isDisabled: () => loading,
    },
    {
      key: 'edit',
      label: 'Edit classroom',
      icon: <IconEdit size={14} />,
      onClick: (row) => beginEdit(row.classroom),
      isDisabled: () => loading,
    },
  ];

  const actions: HeaderAction[] = [
    { key: 'create', label: 'Create New', variant: 'primary', onClick: beginCreate },
    { key: 'refresh', label: 'Refresh', variant: 'secondary', onClick: () => { void loadData(); } },
    { key: 'import', label: 'Import', variant: 'ghost', onClick: () => setMessage('Import flow coming soon.'), disabled: true },
    { key: 'export', label: 'Export', variant: 'ghost', onClick: () => setMessage('Export flow coming soon.'), disabled: true },
  ];

  const detailsTitle = mode === 'create'
    ? 'Create Classroom'
    : mode === 'edit'
    ? 'Editing Classroom'
    : selectedClassroom
    ? selectedClassroom.name
    : 'Select a classroom';

  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';
  const hasEditableTarget = mode === 'create' || Boolean(selectedClassroom);
  const activeCount = rows.filter((row) => row.status === 'Active').length;
  const archivedCount = rows.filter((row) => row.status === 'Archived').length;
  const totalMembers = rows.reduce((sum, row) => sum + row.members, 0);

  const cancelEditing = () => {
    if (mode === 'create') {
      setMode('view');
      setClassroomForm(emptyClassroomForm);
      return;
    }

    if (selectedClassroom) {
      selectClassroom(selectedClassroom);
    }
  };

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          {
            label: 'Classrooms',
            onClick: () => {
              if (isSmallScreen) {
                setMobilePane('list');
              }
            },
          },
          {
            label: mode === 'create' ? 'New Classroom' : selectedClassroom?.name || 'Classrooms',
            isCurrent: true,
          },
        ]}
      />

      <AdminPageHeader
        title="Classrooms"
        subtitle={`${classrooms.length} total • ${selectedIds.length} selected`}
        actions={actions}
      />

      {!hasActiveOrganization && (
        <div
          style={{
            borderRadius: 10,
            border: '1px solid #fcd34d',
            background: '#fffbeb',
            color: '#92400e',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          No organization selected. Select an organization to manage classrooms.
        </div>
      )}

      {(error || message) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || message}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Total" value={rows.length} icon={<IconBook size={15} />} />
        <AdminStatCard label="Active" value={activeCount} icon={<IconCheckCircle size={15} />} accent="active" />
        <AdminStatCard label="Inactive" value={archivedCount} icon={<IconArchive size={15} />} accent="archived" />
        <AdminStatCard label="Members" value={totalMembers} icon={<IconRelationships size={15} />} />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder="Search classrooms by name, code, status..."
        filters={filterConfigs}
      />

      {isSmallScreen && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid #d8e3f6',
            borderRadius: 11,
            background: '#f6f9ff',
            padding: 4,
            width: 'fit-content',
          }}
        >
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('details')}
          >
            Details
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 58%) minmax(320px, 42%)',
          gap: 12,
          minHeight: isNarrowLayout ? 'auto' : 560,
        }}
      >
        {showListPanel && (
          <div style={{ minHeight: 0 }}>
            {loading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>
                Loading classrooms...
              </div>
            ) : (
              <AdminDataGrid<ClassroomRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedClassroomId}
                onRowClick={(row) => beginEdit(row.classroom)}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection={!isSmallScreen}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={sortedRows.length}
                onPageChange={setPage}
                emptyMessage="No classrooms found."
                emptyActionLabel="Create Classroom"
                onEmptyAction={beginCreate}
                rowActions={rowActions}
              />
            )}
          </div>
        )}

        {showDetailsPanel && (
          <div
            style={{
              border: '1px solid #dfe8f8',
              borderRadius: 12,
              background: '#fff',
              boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button
                  type="button"
                  className="amd-btn amd-btn-ghost"
                  style={{ marginBottom: 8 }}
                  onClick={() => setMobilePane('list')}
                >
                  Back to List
                </button>
              )}

              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '1.02rem',
                  fontWeight: 800,
                  color: '#173159',
                  background: hasEditableTarget
                    ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)'
                    : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: hasEditableTarget ? '1px solid #cfe0fb' : '1px solid #d9e2ef',
                  borderRadius: 10,
                  padding: '9px 11px',
                }}
              >
                {detailsTitle}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              {!hasEditableTarget ? (
                <div
                  style={{
                    minHeight: 260,
                    border: '1px dashed #c9d6ef',
                    borderRadius: 12,
                    background: '#f8fbff',
                    display: 'grid',
                    placeContent: 'center',
                    textAlign: 'center',
                    color: '#4f658f',
                    gap: 8,
                    padding: 16,
                  }}
                >
                  <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={26} /></div>
                  <div style={{ fontWeight: 700 }}>Select a classroom from the list</div>
                  <div style={{ fontSize: '0.82rem' }}>or</div>
                  <button type="button" className="amd-btn amd-btn-primary" onClick={beginCreate}>Create a new classroom</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 18 }}>
                  <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                    <AdminSectionHeading icon={<IconGeneral size={14} />} title="General" />
                    <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr', gap: 10 }}>
                      <label style={{ textAlign: 'left' }}>
                        <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Class Name</span>
                        <input
                          value={classroomForm.name}
                          onChange={(e) => setClassroomForm((prev) => ({ ...prev, name: e.target.value }))}
                          disabled={mode === 'view'}
                        />
                      </label>
                      <label style={{ textAlign: 'left' }}>
                        <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Code</span>
                        <input
                          value={classroomForm.code}
                          onChange={(e) => setClassroomForm((prev) => ({ ...prev, code: e.target.value }))}
                          disabled={mode === 'view'}
                        />
                      </label>
                      <label style={{ textAlign: 'left' }}>
                        <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Teacher</span>
                        <select
                          value={classroomForm.teacherUserId}
                          onChange={(e) => setClassroomForm((prev) => ({ ...prev, teacherUserId: e.target.value }))}
                          disabled={mode === 'view'}
                        >
                          <option value="">No teacher</option>
                          {users.map((user) => (
                            <option key={user.userId} value={user.userId}>{user.displayName}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ textAlign: 'left' }}>
                        <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Status</span>
                        <select
                          value={classroomForm.isActive ? 'active' : 'inactive'}
                          onChange={(e) => setClassroomForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}
                          disabled={mode === 'view'}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </label>
                    </div>
                  </section>

                  {selectedClassroom && (
                    <>
                      <section style={{ display: 'grid', gap: 10, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                        <AdminSectionHeading icon={<IconRelationships size={14} />} title="Members" />
                        <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 160px auto', gap: 8 }}>
                          <div>
                            <input
                              placeholder="Search users by name/email/id"
                              value={memberUserSearch}
                              onChange={(e) => setMemberUserSearch(e.target.value)}
                              style={{ width: '100%', marginBottom: 6 }}
                            />
                            <select
                              value={memberUserId ?? ''}
                              onChange={(e) => setMemberUserId(e.target.value ? Number(e.target.value) : null)}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select user</option>
                              {filteredUsers.map((user) => (
                                <option key={user.userId} value={user.userId}>{user.displayName} ({user.userId})</option>
                              ))}
                            </select>
                          </div>
                          <select value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                            <option value="Student">Student</option>
                            <option value="Teacher">Teacher</option>
                            <option value="Assistant">Assistant</option>
                          </select>
                          <button type="button" className="amd-btn amd-btn-secondary" onClick={() => { void addMember(); }} disabled={loading}>Add User</button>
                        </div>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                          {selectedClassroom.members.length === 0 ? (
                            <div style={{ padding: 10, color: '#6b7280' }}>No users assigned yet.</div>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>User</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>Role</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>Status</th>
                                    <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedClassroom.members.map((member) => {
                                    const user = users.find((u) => u.userId === member.userId);
                                    return (
                                      <tr key={member.id}>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                                          {user?.displayName || `User ${member.userId}`}
                                        </td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>{member.role}</td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>{member.status}</td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                                          <button
                                            type="button"
                                            className="amd-btn amd-btn-ghost"
                                            onClick={() => {
                                              requestRemoveMember(member.id, user?.displayName || `User ${member.userId}`);
                                            }}
                                            disabled={loading || removingMemberId === member.id}
                                          >
                                            {removingMemberId === member.id ? 'Removing...' : 'Remove'}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </section>

                      <section style={{ display: 'grid', gap: 10, paddingBottom: 12, borderBottom: '1px solid #edf2fb' }}>
                        <AdminSectionHeading icon={<IconBook size={14} />} title="Courses" />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <select
                            value={courseIdToAdd ?? ''}
                            onChange={(e) => setCourseIdToAdd(e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 1 }}
                          >
                            <option value="">Select course</option>
                            {courses.map((course) => (
                              <option key={course.id} value={course.id}>{course.name}</option>
                            ))}
                          </select>
                          <button type="button" className="amd-btn amd-btn-secondary" onClick={() => { void addCourse(); }} disabled={loading}>Add</button>
                        </div>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                          {selectedClassroom.courses.length === 0 ? (
                            <div style={{ padding: 10, fontSize: 13, color: '#4b5563' }}>No linked courses yet.</div>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>Course</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>Default</th>
                                    <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fbff', fontSize: 12 }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedClassroom.courses.map((item) => {
                                    const course = courses.find((c) => c.id === item.courseId);
                                    return (
                                      <tr key={item.id}>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                                          {course?.name || `Course ${item.courseId}`}
                                        </td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>{item.isDefault ? 'Yes' : 'No'}</td>
                                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                                          <button
                                            type="button"
                                            className="amd-btn amd-btn-ghost"
                                            onClick={() => {
                                              requestRemoveCourse(item.id, course?.name || `Course ${item.courseId}`);
                                            }}
                                            disabled={loading || removingCourseLinkId === item.id}
                                          >
                                            {removingCourseLinkId === item.id ? 'Removing...' : 'Remove'}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </section>

                      <section style={{ display: 'grid', gap: 8 }}>
                        <AdminSectionHeading icon={<IconMetadata size={14} />} title="Metadata" />
                        <div style={{ display: 'grid', gap: 4, fontSize: '0.82rem', color: '#586f96' }}>
                          <div>Classroom ID: <strong>{selectedClassroom.id}</strong></div>
                          <div>Organization ID: <strong>{selectedClassroom.organizationId}</strong></div>
                          <div>Member Count: <strong>{selectedClassroom.members.length}</strong></div>
                        </div>
                      </section>
                    </>
                  )}
                </div>
              )}
            </div>

            {(mode === 'create' || mode === 'edit') && (
              <div
                style={{
                  borderTop: '1px solid #e4ecf9',
                  background: 'rgba(255,255,255,0.98)',
                  padding: '10px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 4,
                }}
              >
                <button type="button" className="amd-btn amd-btn-secondary" onClick={cancelEditing} disabled={loading}>Cancel</button>
                <button
                  type="button"
                  className="amd-btn amd-btn-primary"
                  onClick={() => {
                    if (mode === 'create') {
                      void createClassroom();
                    } else {
                      void updateClassroom();
                    }
                  }}
                  disabled={loading}
                >
                  {mode === 'create' ? 'Create Classroom' : 'Save Changes'}
                </button>
              </div>
            )}

            {mode === 'view' && selectedClassroom && (
              <div style={{ borderTop: '1px solid #e4ecf9', padding: '10px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="amd-btn amd-btn-secondary" onClick={() => beginEdit(selectedClassroom)}>
                  Edit Classroom
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        onClose={closeConfirmDialog}
        actions={confirmDialog?.actions || []}
      />
    </div>
  );
};

export default ManageClassrooms;
